/**
 * @fileoverview Landing page performance table with mock data.
 * Simplified version of PerformanceBySkuTable for showcasing the UI.
 * @module features/landing/components/LandingPerformanceTable
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Thumbnail } from '@/shared/components/ui/Thumbnail'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/shared/hooks/useIsMobile'

/**
 * Props for LandingPerformanceTable component
 */
interface LandingPerformanceTableProps {
  className?: string
}

/**
 * Mock product data for the landing page table (matching LandingMetrics)
 */
const MOCK_PRODUCTS = [
  {
    id: 'monitor-27',
    name: 'Monitor 27" 4K',
    imageUrl: '/landing-page/monitor-mock.webp',
    quantity: 24,
    sales: 68000,
    totalExpenses: 24500,
    profit: 43500,
  },
  {
    id: 'keyboard-pro',
    name: 'Mechanical Keyboard Pro',
    imageUrl: '/landing-page/keyboard-mock.webp',
    quantity: 38,
    sales: 56000,
    totalExpenses: 14400,
    profit: 41600,
  },
  {
    id: 'mouse-gaming',
    name: 'Gaming Mouse Elite',
    imageUrl: '/landing-page/mouse-mock.webp',
    quantity: 62,
    sales: 42000,
    totalExpenses: 5000,
    profit: 37000,
  },
  {
    id: 'microphone-usb',
    name: 'USB Microphone Studio',
    imageUrl: '/landing-page/microphone-mock.webp',
    quantity: 14,
    sales: 8500,
    totalExpenses: 1800,
    profit: 6700,
  },
  {
    id: 'headphones-hd',
    name: 'Wireless Headphones HD',
    imageUrl: '/landing-page/headphones-mock.webp',
    quantity: 35,
    sales: 32000,
    totalExpenses: 35200,
    profit: -3200,
  },
  {
    id: 'studio-monitors',
    name: 'Studio Monitors 5"',
    imageUrl: '/landing-page/studiomonitors-mock.webp',
    quantity: 12,
    sales: 18000,
    totalExpenses: 19600,
    profit: -1600,
  },
  {
    id: 'controller-pro',
    name: 'Gaming Controller Pro',
    imageUrl: '/landing-page/gamingcontroller-mock.webp',
    quantity: 18,
    sales: 12500,
    totalExpenses: 14000,
    profit: -1500,
  },
  {
    id: 'powerbank-20k',
    name: 'Power Bank 20000mAh',
    imageUrl: '/landing-page/powerbank-mock.webp',
    quantity: 6,
    sales: 4300,
    totalExpenses: 5500,
    profit: -1200,
  },
]

/**
 * Formats a number as compact currency (e.g., 195.6K zł)
 */
function formatMoney(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K zł`
  }
  return `${value} zł`
}

/**
 * Landing page performance table component.
 * Displays a simplified product performance table with mock data.
 *
 * @component
 */
export function LandingPerformanceTable({ className }: LandingPerformanceTableProps): ReactElement {
  const { t } = useTranslation('features-landing')
  const isMobile = useIsMobile()

  return (
    <Card className={cn('glass-card p-0 overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <Table className={cn(isMobile && 'table-fixed')}>
          <TableHeader>
            <TableRow className="border-b border-white/5 bg-white/5 hover:bg-white/5">
              <TableHead className={cn(
                'font-medium text-muted-foreground',
                isMobile ? 'p-2 w-14 min-w-14 max-w-14 text-xs' : 'p-4'
              )}>
                {isMobile ? '' : t('demo.performanceTable.productName')}
              </TableHead>
              <TableHead className={cn(
                'font-medium text-right text-muted-foreground',
                isMobile ? 'p-2 text-xs' : 'p-4'
              )}>
                {t('demo.performanceTable.sales')}
              </TableHead>
              <TableHead className={cn(
                'font-medium text-right text-muted-foreground',
                isMobile ? 'p-2 text-xs' : 'p-4'
              )}>
                {t('demo.performanceTable.totalExpenses')}
              </TableHead>
              <TableHead className={cn(
                'font-medium text-right text-muted-foreground',
                isMobile ? 'p-2 text-xs' : 'p-4'
              )}>
                {t('demo.performanceTable.netProfit')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-white/5">
            {MOCK_PRODUCTS.map((product) => (
              <TableRow
                key={product.id}
                className="hover:bg-white/5 transition-colors"
              >
                <TableCell className={cn(
                  isMobile ? 'p-2 w-14 min-w-14 max-w-14' : 'p-4'
                )}>
                  {isMobile ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md">
                          <Thumbnail
                            src={product.imageUrl}
                            alt={product.name}
                            size={36}
                            className="rounded-md ring-1 ring-border/50"
                          />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent side="bottom" align="start" className="w-auto max-w-[200px] p-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium leading-tight text-foreground">
                            {product.name}
                          </span>
                          <span className="text-muted-foreground text-xs tabular-nums">
                            {product.quantity} ×
                          </span>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <div className="flex items-start gap-3 min-w-0">
                      <Thumbnail
                        src={product.imageUrl}
                        alt={product.name}
                        size={40}
                        className="shrink-0 rounded-md ring-1 ring-border/50"
                      />
                      <div className="flex flex-col gap-0.5 min-w-0 justify-center h-10">
                        <span className="text-sm font-medium leading-tight line-clamp-2 text-white/90">
                          {product.name}
                        </span>
                        <span className="text-muted-foreground text-xs font-light tabular-nums">
                          {product.quantity} ×
                        </span>
                      </div>
                    </div>
                  )}
                </TableCell>
                <TableCell className={cn(
                  'text-right font-mono text-foreground',
                  isMobile ? 'p-2 text-xs' : 'p-4'
                )}>
                  {formatMoney(product.sales)}
                </TableCell>
                <TableCell className={cn(
                  'text-right font-mono text-muted-foreground',
                  isMobile ? 'p-2 text-xs' : 'p-4'
                )}>
                  {formatMoney(product.totalExpenses)}
                </TableCell>
                <TableCell className={cn(
                  'text-right font-mono whitespace-nowrap',
                  isMobile ? 'p-2 text-xs' : 'p-4'
                )}>
                  <span
                    className={cn(
                      'font-bold',
                      product.profit >= 0 ? 'text-positive' : 'text-negative'
                    )}
                  >
                    {product.profit >= 0 ? '+' : ''}{formatMoney(product.profit)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  )
}
